/**
 * Accès de secours à la section Déploiement, hors SSO du panel.
 *
 * Quand le backend du panel ne répond plus (MongoDB en panne : /api/auth/check-access en 503),
 * personne ne peut se connecter au panel. La section Déploiement, elle, ne parle qu'au CICD
 * (recube-cicd, token propre `recube.token`) : on la sert sous /secours, protégée par la seule
 * connexion CICD, pour pouvoir relancer un serveur d'API depuis le panel.
 */
export const EMERGENCY_BASE = '/secours';

/**
 * Chemin canonique de l'accès de secours (« /secours » + la suite, brute), ou null si ce n'est
 * pas l'accès de secours.
 *
 * Même décision que le routeur du panel pour sa route /secours/* : react-router décode chaque
 * segment (decodeURIComponent, un « / » décodé reste « %2F », chemin brut si le décodage échoue)
 * puis compare sans tenir compte de la casse. Sans ce décodage, /secour%73 ou /%73ecours
 * passeraient à côté d'App mais tomberaient sur la route /secours/* du panel, qui recharge : boucle.
 */
export function emergencyCanonicalPath(pathname: string): string | null {
  const raw = pathname.split('/');
  let segments: string[];
  try {
    segments = raw.map((s) => decodeURIComponent(s).replace(/\//g, '%2F'));
  } catch {
    segments = raw;
  }
  if (segments[0] !== '' || segments.length < 2 || !/^secours$/i.test(segments[1])) return null;
  // La suite reste brute : le routeur de l'accès de secours la décode lui-même.
  const rest = raw.slice(2).join('/');
  return raw.length > 2 ? `${EMERGENCY_BASE}/${rest}` : EMERGENCY_BASE;
}

/** Chemin de l'accès de secours ? Voir emergencyCanonicalPath. */
export function isEmergencyPath(pathname: string): boolean {
  return emergencyCanonicalPath(pathname) !== null;
}

/**
 * Remet l'adresse sous sa forme canonique (/secours littéral), sans recharger : la base
 * « /secours » du routeur de l'accès de secours se compare au chemin brut, qui doit donc
 * commencer par /secours. Sans effet quand l'adresse est déjà canonique.
 */
export function canonicalizeEmergencyUrl(): void {
  const { pathname, search, hash } = window.location;
  const canonical = emergencyCanonicalPath(pathname);
  if (canonical !== null && canonical !== pathname) {
    window.history.replaceState(window.history.state, '', canonical + search + hash);
  }
}
